<script lang="ts" setup>
    import { onMounted, ref, reactive, computed } from 'vue';
    import { documents } from '../../lib/ChoiceDocumentApi';
    import { IDocumentUnsolicitedQueueResponse, IDocumentIdArray } from '../../lib/Models/DocumentModel';
    import { entityInfo } from '../../lib/ChoiceApi';
    import * as UIEnums from '../../Enums/UiEnums'
    import { DatabaseIdTypes, MessageChannelName, SecurityScreenId, AppScreenNames } from '../../Enums/ChoiceEnums';
    import Toolbar from '../../components/Toolbar.vue';
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground } from '../../lib/Toolbar';
    import * as DateUtils from '../../lib/DateUtils';
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue';
    import '../../../css/choice.css';
    import * as _ from 'lodash';
    import { popAlert, confirmDialog } from '../../lib/Notification';
    import UnsolicitedDocumentSection from '../UnsolicitedDocuments/UnsolicitedDocumentSection.vue';   
    import Pagination from '../../components/Pagination.vue';

    const props = withDefaults(defineProps<{
        canUpdate: number,
        isMarkedForDeletion?: number,
        authUser: string
    }>(), {
    });


    const queueData = reactive<{
        isMarkedForDeletionFlag?: boolean,
        patientId?: number,
        queueListResponseData?: IDocumentUnsolicitedQueueResponse[],
        queuePanelOpen: boolean,
        selectedDocumentsToDelete?: IDocumentIdArray
        documentSelected?: IDocumentUnsolicitedQueueResponse,
        currentTopItem?: number,
        showReconDialog: boolean,
        chkDeleteToggle: boolean,
        searchQueueText?: string,
        showTrashCan: boolean
    }>({
        //default the queue to display Unsolicited NOT marked for deletion if prop is not passed in otherwise
        isMarkedForDeletionFlag: (props.isMarkedForDeletion ?? 0) > 0,
        queuePanelOpen: false,
        currentTopItem: 0,
        showReconDialog: true,
        chkDeleteToggle: true,
        showTrashCan: true
    });

    onMounted(async () => {    
        await loadDocumentQueueList(queueData.isMarkedForDeletionFlag ?? false);
    });
 
    const iconClass = "iconStyle";
    const alertTitle = "Unsolicited Documents";

    enum docToolbar {
        search,
        clearSearch,
        refresh,
        openEmr,
        openIscheduler,
        openEdocument,
        openMarkedForDeletion,
        sortMenu,
        sortOptions1,
        sortOptions2,
        sortOptions3,
        sortOptions4,
        sortOptions5,
        sortOptions6,
        sortOptions7,
        sortOptions8,
        sortOptions9,
        sortOptions10,
        sortOptions11,
        sortOptions12,
        selectMultipleForDelete,
        deleteSelected
    }

    const toolbarConfiguration = ref<IToolbar>({toolbarList: [
        {identifier: docToolbar.search, tooltip: "Search unsolicited document queue", enabled: true, search: {keyPressDelay: false, placeholder: "search", maxLength: 40}},
        {identifier: docToolbar.clearSearch, label: "", width: 38, tooltip: "Clear Search", enabled: true, action: ActionButtonStyle.Remove},
        {identifier: docToolbar.refresh, label: "", width: 38, tooltip: "Refresh Unsolicited Queue", enabled: true, action: ActionButtonStyle.Refresh, performAction: () => loadDocumentQueueList(false)},
        {identifier: docToolbar.openMarkedForDeletion, label: "", width: 38, tooltip: "View documents marked for Deletion", enabled: true, action: ActionButtonStyle.Binoculars, performAction: () => openMarkedForDelete()},
        {identifier: docToolbar.sortMenu, label: "Sort", menuList:[
            {identifier: docToolbar.sortOptions1, label: "Date Created ASC"},
            {identifier: docToolbar.sortOptions2, label: "Date Created DESC"},
            {identifier: docToolbar.sortOptions3, label: "Document ID ASC"},
            {identifier: docToolbar.sortOptions4, label: "Document ID DESC"},
            {identifier: docToolbar.sortOptions5, label: "File Name ASC"},
            {identifier: docToolbar.sortOptions6, label: "File Name DESC"},
            {identifier: docToolbar.sortOptions11, label: "External ID ASC"},
            {identifier: docToolbar.sortOptions12, label: "External ID DESC"}
        ]},
        {identifier: docToolbar.selectMultipleForDelete, width: 38, label: "", action: ActionButtonStyle.Check, tooltip: "Toggle checkmarks on and off for document deletion", enabled: true},
        {identifier: docToolbar.deleteSelected, width: 38, action: ActionButtonStyle.Trash, label: "", tooltip: "Delete Selected Documents from Unsolicited Queue", enabled: queueData.showTrashCan, performAction: () => deleteSelectedDocuments()}
    ]});

    //computed
    const resultCount = computed<number>(() => {
        return queueData.queueListResponseData?.length ?? 0;        
    });

    async function loadDocumentQueueList(isMarkedDel: boolean) {
        queueData.isMarkedForDeletionFlag = isMarkedDel;
        const resp = await documents.getDocumentQueueList(isMarkedDel);
        queueData.queueListResponseData = resp;
        queueData.queueListResponseData = queueData.queueListResponseData.filter(doc => doc.isReconciled === false);  //do not pull in reconciled documents

        //let's loop over records that have a Patient Id and update their name/age/dob/gender
        if (undefined != queueData.queueListResponseData) {
            await Promise.all(queueData.queueListResponseData.map(async (obj) => {
            if (undefined != obj.identifierId || null != obj.identifierId) {
                const patInfo = await entityInfo.getEntityInfoByDatabaseId({ id: obj.identifierId ?? 0, idType: DatabaseIdTypes.patient_id });
                obj.patientFullName = patInfo.patientFullName;
                obj.patientAge = patInfo.patientAge;
                obj.patientDob = patInfo.patientDob;
                obj.patientGender = patInfo.patientGender;
                obj.patientEntityId = patInfo.entityId;
            }
            }));
        }  
        parent.document.getElementById('spnUnsolicitedCount').innerText = resultCount.value.toString();  
    }

    async function displayDocument(docId: number|undefined) {
        queueData.documentSelected = queueData.queueListResponseData?.find(item => item.documentId === docId);

        let patInfo = undefined;
        let patName = undefined;
        let patAge = undefined;
        let patDob = undefined;
        let patGender = undefined;

        if (queueData.documentSelected?.identifierId) {
            patInfo = await entityInfo.getEntityInfoByDatabaseId({ id: queueData.documentSelected?.identifierId ?? 0, idType: DatabaseIdTypes.patient_id });
            patName = patInfo.patientFullName;
            patAge = patInfo.patientAge;
            patDob = patInfo.patientDob;
            patGender = patInfo.patientGender;
        }        

        const filePath = `/officemd/api/documents/file/${queueData.documentSelected?.documentId}`;
        parent.document.getElementById('TabWorkspace1').src = filePath;     

        const divPatientHtml = `<TABLE cellpadding=0 cellspacing=0 border=0 STYLE='TABLE-LAYOUT: fixed; WIDTH: 100%; POSITION: absolute; TOP: 5px;'>
                <TR>
                    <TD align=right width='100%' style='WHITE-SPACE: nowrap; TEXT-OVERFLOW: ellipsis'>
                        <SPAN id=spnPatientName class=emr_portal_patient_name>` + patName + `&nbsp;</SPAN>
                        <SPAN id=spnPatientChart class=emr_portal_patient_name>(` + queueData.documentSelected?.identifierId + `)</SPAN>
                        <SPAN id=spnPatientAgeLabel class=emr_portal_patient_label>&nbsp;&nbsp;Born</SPAN>
                        <SPAN id=spnPatientAge class=emr_portal_patient_info>&nbsp;` + patDob + `(` + patAge +`)</SPAN>
                        <SPAN id=spnPatientGenderLabel class=emr_portal_patient_label>&nbsp;&nbsp;Gender</SPAN>
                        <SPAN id=spnPatientGender class=emr_portal_patient_info>` + patGender + `</SPAN>&nbsp;&nbsp;
                    </TD>
                </TR>
            </TABLE>`;

        const divNoPatientHtml = `<TABLE cellpadding=0 cellspacing=0 border=0 STYLE='TABLE-LAYOUT: fixed; WIDTH: 100%; POSITION: absolute; TOP: 5px;'>
                <TR>
                    <TD align=right width='100%' style='WHITE-SPACE: nowrap; TEXT-OVERFLOW: ellipsis'>
                        <SPAN id=spnPatientName class=emr_portal_patient_name></SPAN>
                        <SPAN id=spnPatientChart class=emr_portal_patient_name></SPAN>
                        <SPAN id=spnPatientAgeLabel class=emr_portal_patient_label></SPAN>
                        <SPAN id=spnPatientAge class=emr_portal_patient_info></SPAN>
                        <SPAN id=spnPatientGenderLabel class=emr_portal_patient_label></SPAN>
                        <SPAN id=spnPatientGender class=emr_portal_patient_info></SPAN>
                    </TD>
                </TR>
            </TABLE>`;

        const jsonString = JSON.stringify(queueData.documentSelected);
            
        let buttonHtml = `<div style="background-color: #DBEBFF; HEIGHT: 23px;">
                    <button class="btn" type="button" style="height: 23px;" onclick="openReconciliationScreen(${docId});"><i class="fas fa-balance-scale" style="color: green;"></i> Reconcile</button>`;
        
        if (!queueData.isMarkedForDeletionFlag) {
            buttonHtml += `<button class="btn" type="button" style="height: 23px;" onclick="deleteDocument(${docId});"><i class="fa-solid fa-trash" style="color: red;"></i> Delete</button>`;                    
        }

        buttonHtml += `<button class="btn" type="button" style="height: 23px;" onclick="viewAudit(${docId});"><i class="fa-solid fa-history" style="color: blue;"></i> Audit</button>`; 
        
        buttonHtml += `</div>`;

        parent.document.getElementById("divDisable").style = "display: ''";
        parent.document.getElementById("divPatient").innerHTML = (null === queueData.documentSelected?.identifierId) ? divNoPatientHtml : divPatientHtml;
        parent.document.getElementById("ActionToolbar").innerHTML = buttonHtml;

    }

    function sinkMessageForDocumentDelete(event: MessageEvent) {
        //Added a broadcast channel to ToDoSuperbill.htm for UnsolicitedDocument that will return the doc id that was just marked for deletion
        //Let's filter down our queue list without going back to the server
        queueData.queueListResponseData = queueData.queueListResponseData?.filter(item => item.documentId != event.data.docId_delete_completed);
    }

    async function searchQueue() : Promise<void> {    
        //filter queue results based on search text - look at file name, file extension, patient name, patient id, dob
        const searchVal = queueData.searchQueueText?.toLowerCase();
        if (searchVal?.length) {
            queueData.queueListResponseData = queueData.queueListResponseData?.filter(
                item => item.documentFileName?.toLowerCase().includes(searchVal) ??
                        item.patientFullName?.toLowerCase().includes(searchVal) ??
                        item.identifierId?.toString().toLowerCase().includes(searchVal) ?? 
                        item.patientDob?.toLowerCase().includes(searchVal)
                );
        }
        else {
            loadDocumentQueueList(false);
        }
    }  

    async function ToolbarSearchText(data: docToolbar, searchResult: string): Promise<void> {
        if (queueData.searchQueueText !== searchResult) {
            queueData.searchQueueText = searchResult;
            searchQueue();
        }
    }  

    function clearSearch() {
        const searchObj = toolbarConfiguration.value.toolbarList[docToolbar.search].search;
        if (searchObj) {
            searchObj.searchText = "";
            queueData.searchQueueText = searchObj?.searchText ?? "";                
        } 
        searchQueue();
    }

    function sortQueue(sortField: string, sortDirection: any) {     
        queueData.queueListResponseData = _.orderBy(queueData.queueListResponseData, [sortField], [sortDirection]);
    }

    function toggleDeleteCheckboxes() {
        const checkboxes = document.querySelectorAll('input[id="chkDelete"]');
        for (const checkbox of checkboxes) {
            checkbox.checked = queueData.chkDeleteToggle;
        }
        queueData.chkDeleteToggle = !queueData.chkDeleteToggle;
    }

    async function deleteSelectedDocuments() {
        //loop over array and detect which ones are checked
        const checkboxes = document.querySelectorAll('input[id="chkDelete"]');            
        let noDocsChecked = true;
        let arrDocIds: number[] = []; 

        for (const checkbox of checkboxes) {
            if (checkbox.checked) {
                noDocsChecked = false;
                arrDocIds.push(parseInt(checkbox.value));
            }            
        }

        if (noDocsChecked) {
            popAlert("You have not selected any documents.", alertTitle);
            return;
        }

        const msg = "Are you sure you want to mark the selected documents as deleted?  They will be permanently deleted after 30 days.";
        const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });    
            
        if (result) {         
            queueData.selectedDocumentsToDelete = {
                ids: arrDocIds
            }

            await documents.deleteDocuments(queueData.selectedDocumentsToDelete, SecurityScreenId.ToDoDocReview, AppScreenNames.ToDoDocReview, props.authUser);

            //filter our queue to remove the deleted docId's without going back to the server
            queueData.queueListResponseData = queueData.queueListResponseData?.filter(item => !arrDocIds.includes(item.documentId ?? 0));

            //update our count
            parent.document.getElementById('spnUnsolicitedCount').innerText = queueData.queueListResponseData?.length;

            //uncheck any checkboxes that have been inadvertently left checked after multiple delete
            for (const checkbox of checkboxes) {
                if (checkbox.checked) {
                    checkbox.checked = false;
                }            
            }

            popAlert("Selected documents have been marked for deletion.", alertTitle);
        }
    }

    async function openMarkedForDelete() {
        queueData.showTrashCan = false;
        loadDocumentQueueList(true);
    }

    async function toolbarClick(data: docToolbar): Promise<void> {
        switch(docToolbar[data]) {            
            case "clearSearch":
                clearSearch();
                break;
            case "sortOptions1":
                sortQueue("dateCreated", "asc");
                break;
            case "sortOptions2":
                sortQueue("dateCreated", "desc");
                break;
            case "sortOptions3":
                sortQueue("documentId", "asc");
                break;
            case "sortOptions4":
                sortQueue("documentId", "desc");
                break;
            case "sortOptions5":
                sortQueue("documentFileName", "asc");
                break;
            case "sortOptions6":
                sortQueue("documentFileName", "desc");
                break;
            case "sortOptions7":
                sortQueue("identifierId", "asc");
                break;
            case "sortOptions8":
                sortQueue("identifierId", "desc");
                break;
            case "sortOptions9":
                sortQueue("patientFullName", "asc");
                break;
            case "sortOptions10":
                sortQueue("patientFullName", "desc");
                break;
            case "sortOptions11":
                sortQueue("externalId", "asc");
                break;
            case "sortOptions12":
                sortQueue("externalId", "desc");
                break;
            case "selectMultipleForDelete":
                toggleDeleteCheckboxes();
                break;
            default:
                return;
        }
    }

</script>

<style>
    div.RowDetail {
        color:  black;
        font-weight: normal;
        font-size: 8pt;
        FONT-FAMILY: Tahoma, Arial,MS Sans Serif, Helvetica, sans-serif;
        white-space: nowrap;
    }
    div.RowHeader {
        color:  black;
        font-weight: bold;
        font-size: 10pt;
        FONT-FAMILY: Tahoma, Arial,MS Sans Serif, Helvetica, sans-serif;
        white-space: nowrap;
    }
    div.RowDetailWarning {
        color:  red;
        font-weight: normal;
        font-size: 8pt;
        FONT-FAMILY: Tahoma, Arial,MS Sans Serif, Helvetica, sans-serif;
        white-space: nowrap;
    }
    .inputText {
        color:  black;
        font-weight: normal;
        font-size: 8pt;
        FONT-FAMILY: Tahoma, Arial,MS Sans Serif, Helvetica, sans-serif;
        white-space: nowrap;
    }
    .iconStyle {
        width: 25px;
        cursor: pointer; 
        background-color: #fff;
    }
    span.RowDetailWarning {
        color:  red;
        font-weight: normal;
        font-size: 8pt;
        FONT-FAMILY: Tahoma, Arial,MS Sans Serif, Helvetica, sans-serif;
        white-space: nowrap;
    }
    .div_center {
        text-align: left;
    }

</style>

<template>
    <div class="row RowDetail ms-1">
        <Toolbar 
            :configuration="toolbarConfiguration" 
            :toolbarSize="ToolbarSize.regular" 
            :toolbarStyle="ToolbarStyle.outline" 
            :toolbarBackground="ToolbarBackground.white"
            @toolbarClick="toolbarClick"
            @toolbarSearchText="ToolbarSearchText"/>
    </div>

    <div class="row RowDetail" v-if="0 === (queueData.queueListResponseData?.length ?? 0)">
        <div class="col">No data returned</div>
    </div>

    <Pagination v-if="(undefined != queueData.queueListResponseData) && (queueData.queueListResponseData?.length ?? 0 > 0)" 
        v-model:currentTopItem="queueData.currentTopItem" 
        :data="queueData.queueListResponseData" 
        :itemsPerPage="10"
        :numberMaxPagesToDisplay="3"
        :alignStyle="UIEnums.AlignmentTypeStyle.Right"
        :buttonSize="UIEnums.PaginationButtonSizeStyle.Small"
    >
        <template #item="{index, item}">
            <UnsolicitedDocumentSection v-bind:item="item" @displayDocument="displayDocument"/>
        </template>        
    </Pagination> 
    
   
    <ChoiceStateMessageSink :channel-name="MessageChannelName.UnsolicitedDocument" @message="sinkMessageForDocumentDelete"></ChoiceStateMessageSink>

</template> 
